import { useQuery } from '@apollo/client'
import { debounce } from 'lodash'
import { useParams } from 'react-router-dom'
import { BucketShare, BucketShareAlbumsDocument, BucketShareType } from '../../../graphql/generated'
import logger from '../../../utils/logger'
import useAlerts from './useAlerts'

export default function useBucketShareAlbums(bucketShare?: BucketShare | null, albumId?: string) {
  const { shareId } = useParams<{ shareId: string }>()
  const { createAlert } = useAlerts()
  const shareType = bucketShare?.shareType

  const results = useQuery(BucketShareAlbumsDocument, {
    skip: !shareId || !shareType || shareType === BucketShareType.Media,
    variables: { shareId: shareId!, albumId },
    onError: (error) => {
      if (error.graphQLErrors.length) {
        error.graphQLErrors.forEach((e) => {
          createAlert(e.message, 'error', e)
        })
        return
      }

      createAlert('There was a problem retrieving your share albums. Please try again later.')
      logger.error(error)
    }
  })

  const items = results.data?.bucketShareAlbums?.items ?? []
  const nextToken = results.data?.bucketShareAlbums?.nextToken
  const loading = !results.called || results.loading

  const getFilteredItems = () => {
    if (shareType === BucketShareType.Bucket) {
      return items.filter((item) => item.bucketId === bucketShare?.bucketId)
    }

    if (shareType === BucketShareType.Album) {
      return items.filter((item) => item.id === bucketShare?.albumId)
    }

    return items
  }

  const albums = getFilteredItems()

  const func = () => {
    if (nextToken) {
      results.fetchMore({ variables: { nextToken } })
    }
  }

  const fetchMore = debounce(func, 500, { leading: true })

  const getShouldRenderShareDrawer = () => {
    if (shareType === BucketShareType.Album) {
      return albums.length > 1 || (albums.length === 1 && albums[0]?.subAlbumCount)
    }

    if (shareType === BucketShareType.Bucket) {
      return Boolean(albums.length)
    }

    return false
  }

  const shouldRenderShareDrawer = getShouldRenderShareDrawer()

  return { items, loading, fetchMore, nextToken, albums, shouldRenderShareDrawer }
}
